import * as React from "react";

import {Decimal} from "decimal.js";
import {AnimatePresence, motion} from "framer-motion";
import ReactMarkdown from "react-markdown";

import * as dialogs from "@client/features/dialogs";
import * as habits from "@client/features/habits";
import {TitleAndDescription} from "@client/features/habits";
import {showFeelerIfFirstTime} from "@client/features/scenes";
import * as workouts from "@client/features/workouts";

import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";

import {ExerciseSetCheck} from "@client/components/ExerciseSet";
import {WorkoutActions} from "@client/components/Workout";
import {primaryButtonClass, secondaryButtonClass} from "@client/components/styles";
import * as widgets from "@client/forms/widgets";
import * as hardware from "@client/hardware";
import {useSerializedSave} from "@client/hooks";
import {BUTTON_CLASS, BUTTON_CLASS_SMALL} from "@client/scenes/Registration";
import * as forms from "@client/site/forms";
import {useAppDispatch} from "@client/store";
import {classNames, server} from "@reactivated";

export const INITIAL_WEIGHTS = {
    "Dumbbell Goblet Squat": {initialLbs: 30, perSide: false, reps: 12},
    "Dumbbell Bench Press": {initialLbs: 20, perSide: true, reps: 12},
    "Dumbbell One-Arm Bent-Over Row": {initialLbs: 20, perSide: true, reps: 12},
    "Dumbbell Biceps Curl": {initialLbs: 15, perSide: true, reps: 12},
    "Dumbbell Overhead Press": {initialLbs: 15, perSide: true, reps: 12},
    "Dumbbell Deadlift": {initialLbs: 30, perSide: true, reps: 12},
    "Dumbbell Triceps Extension": {initialLbs: 20, perSide: false, reps: 12},

    "Barbell Squat": {initialLbs: 135, perSide: false, reps: 5},
    "Barbell Bench Press": {initialLbs: 135, perSide: false, reps: 5},
    "Barbell Row": {initialLbs: 135, perSide: false, reps: 5},
    "Barbell Overhead Press": {initialLbs: 135, perSide: false, reps: 5},
    "Barbell Deadlift": {initialLbs: 135, perSide: false, reps: 5},
};

export function WorkoutCard(props: {
    createWorkout: (source: "BLANK" | UUID) => void;
    profile: server.core.rpc.Profile.output;
    journal: server.journal.day.Journal.output;
    updateJournal: (journal: server.journal.day.Journal.output) => void;
    switchTab: (tab: "workout" | "journal") => void;
    setWorkoutRoute: (route: "program" | "custom") => void;
    calendar: {
        date: string;
        todayString: string;
    };
}) {
    const incompleteWorkout = props.journal.incomplete_workout;
    const [incompleteWorkoutWarning, setIncompleteWorkoutWarning] =
        React.useState(true);

    const nextWorkout = props.journal.program?.workout_groups
        .flatMap((group) => group.workouts)
        .find((workout) => workout.used_on == null);

    const habit = habits.habits.WORKOUT;
    const options = {
        isComplete: props.journal.workout?.status == "COMPLETE",
    };

    /*
    if (props.journal.workout != null) {
        props.journal.workout.name = "Squats, bench and curls";
    }

    if (nextWorkout != null) {
        nextWorkout.name = "Squats, bench and curls";
    }
    props.journal.workout_status = "REST_DAY" as "REST_DAY" | "WORKOUT_DUE";
    */

    const workoutTitle =
        props.calendar.date == props.calendar.todayString
            ? "Today's workout"
            : "Workout";

    const {title, description, actions} =
        incompleteWorkout != null && incompleteWorkoutWarning
            ? {
                  title: "Incomplete workout",
                  description: utils.formatDate(incompleteWorkout.date!),
                  actions: (
                      <>
                          <button
                              type="submit"
                              onClick={() => {
                                  void hardware.startLiveActivity({
                                      workout: incompleteWorkout,
                                  });
                                  router.push("journal.workout", {
                                      key: incompleteWorkout.date!,
                                  });
                              }}
                              className={primaryButtonClass}
                          >
                              Resume
                          </button>
                          <button
                              type="submit"
                              onClick={() => setIncompleteWorkoutWarning(false)}
                              className={secondaryButtonClass}
                          >
                              Ignore
                          </button>
                      </>
                  ),
              }
            : props.journal.workout?.status == "COMPLETE"
              ? {
                    title: workoutTitle,
                    description: props.journal.workout.name ?? "Custom workout",
                    actions: (
                        <button
                            type="submit"
                            onClick={() => {
                                if (props.journal.workout == null) return; // Should not happen
                                props.switchTab("workout");
                            }}
                            className={secondaryButtonClass}
                        >
                            View workout
                        </button>
                    ),
                }
              : props.journal.workout != null
                ? {
                      title: workoutTitle,
                      description: props.journal.workout.name ?? "Custom workout",
                      actions: (
                          <button
                              type="submit"
                              onClick={() => {
                                  if (props.journal.workout == null) return; // Should not happen
                                  void hardware.startLiveActivity({
                                      workout: props.journal.workout,
                                  });
                                  props.switchTab("workout");
                              }}
                              className={primaryButtonClass}
                          >
                              Resume workout
                          </button>
                      ),
                  }
                : props.journal.workout_status == "REST_DAY" || nextWorkout == null
                  ? {
                        title: workoutTitle,
                        description: "No programmed workout",
                        actions: (
                            <>
                                {nextWorkout != null && (
                                    <typography.AsyncButton
                                        type="submit"
                                        onClick={() =>
                                            props.createWorkout(nextWorkout.uuid)
                                        }
                                        className={secondaryButtonClass}
                                    >
                                        Do next anyway
                                    </typography.AsyncButton>
                                )}
                                <button
                                    type="submit"
                                    onClick={() => {
                                        props.switchTab("workout");
                                        props.setWorkoutRoute("custom");
                                    }}
                                    className={secondaryButtonClass}
                                >
                                    Custom workout
                                </button>
                            </>
                        ),
                    }
                  : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    props.journal.workout_status == "WORKOUT_DUE" && nextWorkout != null
                    ? {
                          title: workoutTitle,
                          description: nextWorkout.name ?? "",
                          actions: (
                              <>
                                  <typography.AsyncButton
                                      className={primaryButtonClass}
                                      onClick={() => {
                                          void analytics.track(
                                              "SELECTED_PROGRAM_WORKOUT",
                                          );
                                          return props.createWorkout(nextWorkout.uuid);
                                      }}
                                  >
                                      Start
                                  </typography.AsyncButton>
                                  {/*
                <button
                    onClick={() => alert("This feature is coming soon.")}
                    className="rounded-sm bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                    Tomorrow
                </button>
                */}
                                  <typography.AsyncButton
                                      onClick={() => {
                                          void analytics.track(
                                              "SELECTED_CUSTOM_WORKOUT",
                                          );
                                          props.switchTab("workout");
                                          props.setWorkoutRoute("custom");
                                      }}
                                      className={secondaryButtonClass}
                                  >
                                      Custom workout
                                  </typography.AsyncButton>
                              </>
                          ),
                      }
                    : {
                          title: workoutTitle,
                          description: "This never happens",
                          actions: <div>This never happens</div>,
                      };

    const content = (
        <TitleAndDescription
            title={title}
            description={description}
            actions={actions}
        />
    );

    return (
        <habits.JournalHabitShell
            label="Workout"
            habit={habit}
            content={content}
            isComplete={options.isComplete}
        />
    );
}

function Page(props: {animation: "in" | "out"; children: React.ReactNode}) {
    const transition = {ease: "easeInOut", duration: 0.3};

    return (
        <motion.div
            className="absolute inset-4 inset-y-0 overflow-x-hidden overflow-y-auto"
            initial={{x: props.animation == "in" ? "-100%" : "100%", opacity: 0}}
            animate={{x: 0, opacity: 1}}
            exit={{x: props.animation == "in" ? "-100%" : "100%", opacity: 0}}
            transition={transition}
        >
            {props.children}
        </motion.div>
    );
}

function getSuggestedWeight(
    isImperial: boolean,
    weightDone: string,
    repsDone: number,
    targetReps: number,
) {
    // Convert weight to Decimal for precise calculations
    const weight = new Decimal(weightDone);

    // Calculate 1RM using Epley formula: 1RM = weight * (1 + reps/30)
    const oneRM = weight.mul(new Decimal("1").add(new Decimal(repsDone).div("30")));

    // Reverse the formula to get target weight:
    // targetWeight = 1RM / (1 + targetReps/30)
    const targetWeight = oneRM.div(
        new Decimal("1").add(new Decimal(targetReps).div("30")),
    );

    return utils
        .roundNumberToNearest(
            new Decimal(targetWeight).dividedBy(isImperial ? "0.45359237" : "1"),
            targetWeight.lt("15") ? new Decimal("2.5") : new Decimal("5"),
        )
        .times(isImperial ? "0.45359237" : "1")
        .toString();
}

function ExerciseSet(props: {
    exercise: workouts.Exercise;
    set: workouts.ExerciseSet;
    hideLabel?: boolean;
}) {
    const {set} = props;
    const isImperial = forms.useIsImperial();
    const readOnly = false;

    return (
        <div className="flex gap-3">
            <div className={classNames("bg-white px-3 rounded-md flex-1", "py-3")}>
                <label className="flex flex-col gap-2">
                    <span
                        className={classNames(
                            "uppercase text-xs font-medium",
                            props.hideLabel == true && "sr-only",
                        )}
                    >
                        Weight
                    </span>
                    {/* We use the status to remount in case we accept the suggested weight. */}
                    <div className="flex items-end gap-2">
                        <div
                            className="
                                flex items-center rounded-md bg-white px-3 outline-1
                                -outline-offset-1 outline-gray-300 outline-solid
                                focus-within:outline-1 focus-within:-outline-offset-2
                                focus-within:outline-indigo-600
                                focus-within:outline-solid
                            "
                            onFocus={forms.handleInputFocusSelectAndPreventContextMenu}
                        >
                            <widgets.WeightInput
                                className="
                                    w-20 border-none p-2 pr-0! text-2xl/4 font-light
                                    focus:[box-shadow:none]
                                "
                                key={`${set.status}-${set.uuid}`}
                                isImperial={isImperial}
                                name={React.useId()}
                                value={set.weight}
                                onChange={(value) => set.__update({weight: value})}
                                placeholder={
                                    set.status == "INCOMPLETE"
                                        ? (set.suggested_weight ?? undefined)
                                        : undefined
                                }
                                readOnly={readOnly}
                            />
                            <span
                                className="
                                    text-base font-light text-gray-500 select-none
                                "
                            >
                                {isImperial ? "lbs" : "kg"}
                            </span>
                        </div>
                    </div>
                </label>
            </div>
            <div className={classNames("bg-white px-3 rounded-md flex-1", "py-3")}>
                <label className="flex flex-col gap-2">
                    <span
                        className={classNames(
                            "uppercase text-xs font-medium",
                            props.hideLabel == true && "sr-only",
                        )}
                    >
                        Repetitions
                    </span>
                    <div onFocus={forms.handleInputFocusSelectAndPreventContextMenu}>
                        <widgets.DecimalInput
                            className="
                                w-full rounded-md border border-gray-300 p-2 text-2xl/4
                                font-light
                                focus:ring-0 focus:ring-indigo-600 focus:ring-offset-0
                            "
                            name={React.useId()}
                            value={set.reps?.toString() ?? ""}
                            integerOnly
                            readOnly={readOnly}
                            onChange={(value) => {
                                if (value == null) {
                                    set.__update({reps: value});
                                } else {
                                    set.__update({
                                        reps: parseInt(value),
                                    });
                                }
                            }}
                        />
                    </div>
                </label>
            </div>
        </div>
    );
}

export function GuidedWorkout(props: {
    journal: server.journal.day.Journal.output;
    workout: server.journal.workouts.Workout.output;
    updateJournal: (
        value: (
            prev: server.journal.day.Journal.output,
        ) => server.journal.day.Journal.output,
    ) => void;
    switchTab: (tab: "workout" | "journal") => void;
    deleteWorkout: () => Promise<void>;
    startRestTimer: (duration: number | null) => void;
    restTimer: number | null;
    scope: "client" | "consumer";
}) {
    const dispatch = useAppDispatch();
    const workout = workouts.useWorkout({
        workout: props.workout,
        setWorkout: (value) => {
            props.updateJournal((prev) => ({
                ...prev,
                workout: prev.workout == null ? null : value(prev.workout),
            }));
        },
    });

    const videoDialog = dialogs.useDialog<unknown, {video_uid: string}>({
        Content: ({video_uid}) => (
            <div className="relative aspect-video w-[95vw] max-w-[1024px]">
                <div className="absolute inset-0 z-1 flex items-center justify-center">
                    <motion.div
                        className="
                            size-6 rounded-full border-2 border-gray-300
                            border-t-indigo-700 will-change-transform
                        "
                        animate={{rotate: 360}}
                        transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                        }}
                    />
                </div>
                <iframe
                    src={`https://customer-zze5w8c396zl9f2i.cloudflarestream.com/${video_uid}/iframe`}
                    style={{border: "none"}}
                    className="relative z-10 size-full"
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                />
            </div>
        ),
    });

    const guideDialog = dialogs.useDialog<unknown, {instructions: string}>({
        Content: (props) => {
            return (
                <div className="max-w-(--breakpoint-sm) p-4">
                    <ReactMarkdown className={"legacy-page"}>
                        {props.instructions}
                    </ReactMarkdown>
                </div>
            );
        },
    });

    const isImperial = forms.useIsImperial();
    const initial = React.useRef(true);
    const completed = React.useRef(false);
    const [shouldCompleteWorkout, setShouldCompleteWorkout] = React.useState(false);

    const {flush} = useSerializedSave(
        async () => {
            if (initial.current == true) {
                initial.current = false;
                // We do want to save on load for the initial weight pre-population.
            }

            if (completed.current == true) {
                return;
            }

            await server.journal.workouts.update_workout({
                uuid: props.workout.uuid,
                sections: workout.sections,
                questions: workout.questions,
                template: null,
            });
        },
        initial.current == true ? 0 : 300,
        [workout.sections, workout.questions],
    );

    const completeWorkout = async () => {
        props.startRestTimer(null);
        await flush();
        completed.current = true;

        props.updateJournal((prev) => ({
            ...prev,
            workout:
                prev.workout == null ? null : {...prev.workout, status: "COMPLETE"},
        }));
        void server.journal.workouts.complete_workout({
            workout_uuid: props.workout.uuid,
        });
        void hardware.endLiveActivity();
        dispatch(showFeelerIfFirstTime());
        void analytics.track("COMPLETED_WORKOUT", {
            workout_template_name: props.workout.name ?? undefined,
        });
        props.switchTab("journal");
    };

    // Completing the last set queues the state change, so finishing waits a
    // render for the sections snapshot to catch up.
    React.useEffect(() => {
        if (shouldCompleteWorkout == true) {
            void completeWorkout();
        }
    }, [shouldCompleteWorkout]);

    const [currentExercise, setCurrentExercise] = React.useState(() => {
        const nextExerciseIndex = workout.exercises.findIndex((exercise) =>
            exercise.sets.some((set) => set.status == "INCOMPLETE"),
        );

        if (nextExerciseIndex == -1) {
            return workout.exercises.length - 1;
        }

        return nextExerciseIndex;
    });

    const [activeSetIndex, setActiveSetIndex] = React.useState(() => {
        const nextSetIndex = workout.exercises[currentExercise].sets.findIndex(
            (set) => set.status == "INCOMPLETE",
        );

        if (nextSetIndex == -1) {
            return workout.exercises[currentExercise].sets.length - 1;
        }

        return nextSetIndex;
    });

    const nextExercise = () => {
        props.startRestTimer(null);
        const nextExercise = currentExercise + 1;
        setCurrentExercise(nextExercise);
        setActiveSetIndex(
            workout.exercises[nextExercise].sets.findIndex(
                (set) => set.status == "INCOMPLETE",
            ),
        );
    };

    const previousExercise = () => {
        props.startRestTimer(null);
        const previousExercise = currentExercise - 1;
        setCurrentExercise(previousExercise);
        setActiveSetIndex(workout.exercises[previousExercise].sets.length - 1);
    };

    const completeSet = (
        exercise: workouts.Exercise,
        set: workouts.ExerciseSet,
        index: number,
    ) => {
        initial.current = true;

        if (set.reps == null || set.weight == null) {
            return;
        }

        const nextSet = [exercise.sets[index + 1], null][0];

        set.__update({
            status: "COMPLETE",
            weight: set.weight,
        });

        if (nextSet != null) {
            // Not the last set of the exercise
            const hasRestDuration =
                exercise.rest_duration != null && exercise.rest_duration > 0;

            if (hasRestDuration) {
                props.startRestTimer(exercise.rest_duration);
            }

            const exerciseSettings =
                INITIAL_WEIGHTS[exercise.type.name as keyof typeof INITIAL_WEIGHTS];

            nextSet.__update({
                weight: getSuggestedWeight(
                    isImperial,
                    set.weight,
                    set.reps,
                    exerciseSettings.reps,
                ),
            });
            setActiveSetIndex(index + 1);
        } else if (currentExercise != workout.exercises.length - 1) {
            // Last set of exercise, but not last exercise
            props.startRestTimer(null);
            nextExercise();
        } else {
            // Last set of the entire workout - clear any active rest timer
            props.startRestTimer(null);
            setShouldCompleteWorkout(true);
        }
    };

    const isLastExercise = currentExercise == workout.exercises.length - 1;
    const isLastSet =
        activeSetIndex == workout.exercises[currentExercise].sets.length - 1;

    const isLastExerciseAndSet = isLastExercise && isLastSet;

    const activeSet = [
        workout.exercises[currentExercise].sets[activeSetIndex],
        null,
    ][0];

    const appInfo = React.use(hardware.getAppInfo());

    /*
    React.useEffect(() => {
        if (
            activeSet == 0 &&
            workout.exercises[currentExercise].sets[activeSet].weight == null
        ) {
            const suggestedWeight = new Decimal(progressionStart)
                .times(isImperial ? "0.45359237" : "1")
                .toString();

            workout.exercises[currentExercise].sets[activeSet].__update({
                weight: suggestedWeight,
            });
            setForceSetRender(forceSetRender + 1);
        }
    }, []);
    */

    const [isEditing, _setIsEditing] = React.useState(false);

    const setIsEditing = (value: boolean) => {
        if (appInfo.platform == "web") {
            return;
        }
        // https://stackoverflow.com/questions/33853924/removing-wkwebview-accesory-bar-in-swift/58001395#58001395
        // https://archive.ph/AZfNT
        // https://trac.webkit.org/changeset/246229/webkit#file1

        _setIsEditing(value);
    };

    const renderSets = (exercise: workouts.Exercise) => {
        const exerciseSettings =
            INITIAL_WEIGHTS[exercise.type.name as keyof typeof INITIAL_WEIGHTS];

        if (activeSet == null) {
            throw new Error("Active set is null");
        }

        if (exercise.mode == "GUIDED") {
            return (
                <>
                    <div className={classNames("flex-1 flex flex-col justify-center")}>
                        <AnimatePresence initial={false}>
                            {isEditing == false && (
                                <motion.div
                                    initial={{opacity: 0}}
                                    animate={{
                                        opacity: 1,
                                        transition: {
                                            duration: 0.4,
                                            delay: 0.25,
                                        },
                                    }}
                                    exit={{
                                        opacity: 0,
                                        transition: {duration: 0.5},
                                    }}
                                    className="
                                        flex h-full flex-1 flex-col justify-between py-3
                                        text-lg
                                    "
                                >
                                    <ol
                                        className="
                                            flex flex-1 list-outside list-decimal
                                            flex-col justify-between pl-8 text-xl/7
                                            text-pretty
                                        "
                                    >
                                        {activeSetIndex == 0 ? (
                                            <>
                                                <li>
                                                    Start with{" "}
                                                    <strong
                                                        className="
                                                            font-semibold
                                                            text-indigo-700
                                                        "
                                                    >
                                                        {utils.lbOrKg(
                                                            activeSet.weight,
                                                            isImperial,
                                                        )}
                                                    </strong>{" "}
                                                    {exerciseSettings.perSide ? (
                                                        <em>per side</em>
                                                    ) : (
                                                        ""
                                                    )}{" "}
                                                    and do{" "}
                                                    <strong
                                                        className="
                                                            font-semibold
                                                            text-indigo-700
                                                        "
                                                    >
                                                        as many reps
                                                    </strong>{" "}
                                                    as you can.
                                                </li>
                                                <li>
                                                    Stop when your form breaks down.
                                                </li>
                                                <li>
                                                    Can’t find a weight?
                                                    <br />
                                                    Use the closest you can find.
                                                </li>
                                            </>
                                        ) : (
                                            <>
                                                <li>Rest at least two minutes.</li>
                                                <li>
                                                    Then use{" "}
                                                    <strong
                                                        className="
                                                            font-semibold
                                                            text-indigo-700
                                                        "
                                                    >
                                                        {utils.lbOrKg(
                                                            activeSet.weight,
                                                            isImperial,
                                                        )}
                                                    </strong>{" "}
                                                    {exerciseSettings.perSide ? (
                                                        <em>per side</em>
                                                    ) : (
                                                        ""
                                                    )}{" "}
                                                    and try to get at least{" "}
                                                    <strong
                                                        className="
                                                            font-semibold
                                                            text-indigo-700
                                                        "
                                                    >
                                                        {exerciseSettings.reps} reps
                                                    </strong>
                                                    .
                                                </li>
                                                <li>
                                                    Stop when your form breaks down.
                                                </li>
                                            </>
                                        )}
                                    </ol>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <div
                        onFocus={() => setIsEditing(true)}
                        onBlur={async () => {
                            await utils.delay(100);
                            setIsEditing(false);
                        }}
                    >
                        <h4 className="mb-2 text-3xl font-light">
                            Set {activeSetIndex + 1} / {exercise.sets.length}
                        </h4>
                        <ExerciseSet exercise={exercise} set={activeSet} />
                        <div className="mt-2 flex flex-col gap-3">
                            <div className="flex">
                                <button
                                    className={classNames(
                                        "px-4 text-xl",
                                        currentExercise == 0 && activeSetIndex == 0
                                            ? "invisible"
                                            : "",
                                    )}
                                    disabled={
                                        currentExercise == 0 && activeSetIndex == 0
                                    }
                                    onClick={() => {
                                        if (activeSetIndex == 0) {
                                            previousExercise();
                                        } else {
                                            setActiveSetIndex(activeSetIndex - 1);
                                        }
                                    }}
                                >
                                    ←
                                </button>
                                <button
                                    disabled={
                                        activeSet.weight == null ||
                                        activeSet.reps == null
                                    }
                                    type="button"
                                    className={classNames(
                                        BUTTON_CLASS_SMALL,
                                        "flex-1 max-w-64",
                                    )}
                                    onClick={() => {
                                        completeSet(
                                            exercise,
                                            activeSet,
                                            activeSetIndex,
                                        );
                                    }}
                                >
                                    {isLastExerciseAndSet
                                        ? "Finish workout"
                                        : isLastSet
                                          ? "Next exercise"
                                          : "Next"}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            );
        }

        return (
            <div key={exercise.uuid}>
                <div className="grid grid-cols-[auto_1fr_auto] items-end gap-3">
                    {exercise.sets.map((set, setIndex) => {
                        return (
                            <React.Fragment key={set.uuid}>
                                <div className="text-left text-2xl">{setIndex + 1}</div>
                                <div>
                                    <ExerciseSet
                                        exercise={exercise}
                                        set={set}
                                        hideLabel={setIndex != 0}
                                    />
                                </div>
                                <div className="pb-5">
                                    <ExerciseSetCheck
                                        handler={set}
                                        readOnly={
                                            set.weight == null || set.reps == null
                                        }
                                        isImperial={isImperial}
                                        restTimerBetweenExercises={false}
                                    />
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>

                <div className="mt-3 mb-16 flex">
                    <button
                        className={classNames(
                            "px-4 text-xl",
                            currentExercise == 0 ? "invisible" : "",
                        )}
                        disabled={currentExercise == 0}
                        onClick={() => {
                            previousExercise();
                        }}
                    >
                        ←
                    </button>
                    <button
                        disabled={
                            exercise.sets.some((set) => set.weight == null) ||
                            exercise.sets.some((set) => set.reps == null) ||
                            exercise.sets.some((set) => set.status == "INCOMPLETE")
                        }
                        type="button"
                        className={classNames(BUTTON_CLASS_SMALL, "flex-1 max-w-64")}
                        onClick={() => {
                            if (isLastExercise) {
                                setShouldCompleteWorkout(true);
                            } else {
                                nextExercise();
                            }
                        }}
                    >
                        {isLastExercise ? "Finish workout" : "Next exercise"}
                    </button>
                </div>
            </div>
        );
    };

    const [needsToViewInstructions, setNeedsToViewInstructions] = React.useState<
        "first" | "second" | null
    >(
        workout.exercises.every((exercise) => exercise.mode == "GUIDED") &&
            workout.exercises.every((exercise) =>
                exercise.sets.every((set) => set.status == "INCOMPLETE"),
            )
            ? "first"
            : workout.exercises.some((exercise) => exercise.mode == "GUIDED") &&
                workout.exercises.every((exercise) =>
                    exercise.sets.every((set) => set.status == "INCOMPLETE"),
                )
              ? "second"
              : null,
    );

    const exercisesPreview = (
        <>
            {workout.exercises.map((exercise, index) => {
                return (
                    <div key={exercise.uuid} className="flex items-center gap-2 p-3">
                        <span
                            className="
                                flex size-7 items-center justify-center rounded-full
                                border border-[#7D50FF] text-sm font-semibold
                                text-[#7D50FF]
                            "
                        >
                            {index + 1}
                        </span>
                        <h2>{exercise.type.name}</h2>
                    </div>
                );
            })}

            <div className="flex gap-2">
                {/*}
                    <button className={classNames(BUTTON_CLASS)}>
                        Edit
                    </button>
                    */}
                <button
                    className={classNames(BUTTON_CLASS, "w-full")}
                    onClick={() => setNeedsToViewInstructions(null)}
                >
                    Got it
                </button>
            </div>
        </>
    );
    const modalClass =
        "absolute inset-0 bg-white flex flex-col justify-between pb-safe pt-4 text-pretty px-4 text-center z-10 rounded-xl";

    const firstInstructions = (
        <motion.div
            className={modalClass}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.5}}
        >
            <h1 className="text-3xl font-extralight">Your first workout</h1>
            <p>
                This is your first workout, so we’re going to find out how much weight
                you can lift safely.
            </p>
            <p>
                We’ll use the results to customize your workout plan to help you get
                stronger every week.
            </p>
            {exercisesPreview}
        </motion.div>
    );

    const secondInstructions = (
        <motion.div
            className={modalClass}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.5}}
        >
            <h1 className="text-3xl font-extralight">Your second workout</h1>
            <p>
                The squat repeats every workout.
                <br />
                It’s <strong>that</strong> important.
            </p>
            <p>
                Based on your squat results from last workout, we’ve customized your
                weight and reps.
            </p>
            <p>
                After squatting, we’ll guide you through the remaining exercises for the
                first time.
            </p>
            {exercisesPreview}
        </motion.div>
    );

    return (
        <Page key="workout" animation="out">
            <AnimatePresence initial={false}>
                {needsToViewInstructions == "first" && firstInstructions}
            </AnimatePresence>
            <AnimatePresence initial={false}>
                {needsToViewInstructions == "second" && secondInstructions}
            </AnimatePresence>
            <WorkoutActions
                actions={[
                    {
                        icon: "0018-eraser",
                        title: "Delete workout",
                        onClick: () => props.deleteWorkout(),
                    },
                ]}
            />
            <div className="flex h-full flex-col">
                {videoDialog.dialog}
                {guideDialog.dialog}
                <div className="flex flex-1 flex-col overflow-x-hidden">
                    <workouts.WorkoutContext.Provider
                        value={{
                            workout: props.workout,
                            scope: props.scope,
                            startRestTimer: props.startRestTimer,
                        }}
                    >
                        <AnimatePresence initial={false} mode="popLayout">
                            {workout.exercises
                                .filter((exercise, index) => index == currentExercise)
                                .map((exercise) => (
                                    <motion.div
                                        key={exercise.uuid}
                                        initial={{x: "100%"}}
                                        animate={{x: 0}}
                                        exit={{x: "-100%"}}
                                        transition={{
                                            type: "spring",
                                            duration: 1,
                                            delay: 0,
                                        }}
                                        className={classNames(
                                            "w-full flex-1 flex flex-col py-0",
                                            "@container",
                                        )}
                                    >
                                        <workouts.ExerciseContext.Provider
                                            value={{exercise}}
                                        >
                                            {exercise.type.media?.image_uid != null && (
                                                <div
                                                    className="
                                                        relative -mx-2 flex
                                                        justify-center py-0
                                                    "
                                                >
                                                    {exercise.type.media
                                                        .wide_image_uid != null && (
                                                        <div
                                                            className="
                                                                mx-2.5 w-full rounded-xl
                                                                bg-white
                                                            "
                                                        >
                                                            <img
                                                                className="w-full"
                                                                src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${exercise.type.media.wide_image_uid}/public`}
                                                            />
                                                        </div>
                                                    )}
                                                    {exercise.type.media
                                                        .wide_image_uid == null && (
                                                        <div
                                                            className="
                                                                mx-2 flex w-full
                                                                justify-center
                                                                rounded-xl bg-white py-4
                                                            "
                                                        >
                                                            <img
                                                                className="
                                                                    h-[20dvh]
                                                                    object-contain
                                                                "
                                                                src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${exercise.type.media.image_uid}/public`}
                                                            />
                                                        </div>
                                                    )}
                                                    <div
                                                        className="
                                                            absolute right-4 bottom-2
                                                            flex flex-col gap-1
                                                        "
                                                    >
                                                        {exercise.type.media
                                                            .video_uid != null && (
                                                            <button
                                                                type="button"
                                                                className={
                                                                    primaryButtonClass
                                                                }
                                                                onClick={() => {
                                                                    void analytics.track(
                                                                        "EXERCISE_VIDEO_PLAYED",
                                                                    );
                                                                    void videoDialog({
                                                                        video_uid:
                                                                            exercise
                                                                                .type
                                                                                .media!
                                                                                .video_uid!,
                                                                    });
                                                                }}
                                                            >
                                                                <div
                                                                    className="
                                                                        flex
                                                                        items-center
                                                                        gap-1
                                                                    "
                                                                >
                                                                    <typography.SimpleIcon icon="0875-play-circle" />
                                                                    Video
                                                                </div>
                                                            </button>
                                                        )}
                                                        {exercise.type.description !=
                                                            "" && (
                                                            <button
                                                                type="button"
                                                                className={
                                                                    secondaryButtonClass
                                                                }
                                                                onClick={() => {
                                                                    void analytics.track(
                                                                        "CLICKED_EXERCISE_TYPE_HELP",
                                                                    );
                                                                    void guideDialog({
                                                                        instructions:
                                                                            exercise
                                                                                .type
                                                                                .description,
                                                                    });
                                                                }}
                                                            >
                                                                <div
                                                                    className="
                                                                        flex
                                                                        items-center
                                                                        gap-1
                                                                    "
                                                                >
                                                                    <typography.SimpleIcon icon="custom-0283-contacts" />
                                                                    Guide
                                                                </div>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="pb-4">
                                                <h2
                                                    className="mt-2 font-light"
                                                    style={{
                                                        fontSize: utils.getFontSizeVW(
                                                            exercise.type.name,
                                                        ),
                                                    }}
                                                >
                                                    {exercise.type.name}
                                                </h2>
                                                <h4
                                                    className="
                                                        text-sm font-normal
                                                        text-gray-600
                                                    "
                                                >
                                                    Exercise {currentExercise + 1} of{" "}
                                                    {workout.exercises.length}
                                                </h4>
                                            </div>
                                            {renderSets(exercise)}
                                        </workouts.ExerciseContext.Provider>
                                    </motion.div>
                                ))}
                        </AnimatePresence>
                    </workouts.WorkoutContext.Provider>
                </div>
            </div>
        </Page>
    );
}
